import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API, formatCPF, onlyDigits } from "@/lib/api";
import { logger } from "@/lib/logger";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Search, Download, KeyRound, ClipboardEdit, Copy, Loader2, IdCard, Hash } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";

const statusColor = (s) => ({
  "Recebida": "bg-blue-100 text-blue-800",
  "Em Triagem": "bg-yellow-100 text-yellow-800",
  "Documentação Pendente": "bg-orange-100 text-orange-800",
  "Em Análise": "bg-purple-100 text-purple-800",
  "Aprovada": "bg-green-100 text-green-800",
  "Indeferida": "bg-red-100 text-red-800",
  "Homologada": "bg-emerald-100 text-emerald-800",
}[s] || "bg-slate-100");

function UpdatePasswordDialog({ open, onOpenChange, protocolo }) {
  const nav = useNavigate();
  const [cpf, setCpf] = useState("");
  const [senha, setSenha] = useState("");
  const [geradaInfo, setGeradaInfo] = useState(null);
  const [loading, setLoading] = useState(false);

  const gerarSenha = async () => {
    const cpfDigits = onlyDigits(cpf);
    if (cpfDigits.length !== 11) {
      toast.error("Informe o CPF do titular antes de gerar a senha");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post(`/inscricoes/protocolo/${protocolo}/gerar-senha`,
                                 { cpf: cpfDigits });
      setGeradaInfo(r.data);
      toast.success(r.data.mensagem || "Se os dados conferem, a senha foi enviada por e-mail");
    } catch (e) {
      logger.error("Falha ao gerar senha", e);
      toast.error(e.response?.data?.detail || "Erro ao gerar senha");
    } finally { setLoading(false); }
  };

  const entrar = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/inscricoes/protocolo/login-atualizacao", {
        protocolo, cpf: onlyDigits(cpf), senha,
      });
      // SEC: dados sensíveis passam apenas pelo navigation state (memória do
      // react-router), nunca por sessionStorage/localStorage. Se o usuário
      // recarregar/fechar a aba, é obrigado a reautenticar — comportamento
      // esperado para um token single-use de 30min.
      toast.success("Acesso liberado! Redirecionando...");
      onOpenChange(false);
      nav("/atualizar", { state: { token: r.data.token, session: r.data } });
    } catch (err) {
      logger.error("Falha login atualização", err);
      toast.error(err.response?.data?.detail || "CPF ou senha inválidos");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="update-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ClipboardEdit className="w-5 h-5 text-[#083CA6]" />Atualização Cadastral</DialogTitle>
          <DialogDescription>
            Protocolo: <span className="font-mono font-semibold">{protocolo}</span>
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={entrar} className="space-y-3">
          <div>
            <Label htmlFor="upd-cpf">CPF</Label>
            <Input id="upd-cpf" value={cpf} onChange={(e) => setCpf(formatCPF(e.target.value))} placeholder="000.000.000-00" required data-testid="upd-cpf" />
          </div>
          <div>
            <Label htmlFor="upd-senha">Senha temporária</Label>
            <Input id="upd-senha" value={senha} onChange={(e) => setSenha(e.target.value.toUpperCase())} placeholder="Ex: A7K3M9P2" required data-testid="upd-senha" />
          </div>

          <div className="border border-dashed rounded p-3 bg-slate-50">
            <Button type="button" variant="outline" onClick={gerarSenha} disabled={loading} className="w-full" data-testid="btn-gerar-senha">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
              Gerar senha
            </Button>
            {geradaInfo && (
              <Alert className="mt-3 border-green-400 bg-green-50" data-testid="senha-gerada-alert">
                <AlertDescription className="text-xs space-y-1">
                  <div><strong>Senha gerada!</strong> {geradaInfo.mensagem}</div>
                  {geradaInfo.email_destino && <div>📧 {geradaInfo.email_destino}</div>}
                  {geradaInfo.senha_mock && (
                    <div className="flex items-center gap-2 pt-1">
                      <span className="text-xs text-slate-500">Senha (mock):</span>
                      <code className="bg-white px-2 py-0.5 rounded text-sm font-bold" data-testid="senha-mock-valor">{geradaInfo.senha_mock}</code>
                      <button type="button" onClick={() => { navigator.clipboard?.writeText(geradaInfo.senha_mock); toast.success("Senha copiada"); }} className="text-slate-500 hover:text-[#083CA6]">
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                  <div className="text-slate-500">Expira em {geradaInfo.expira_em_minutos} minutos.</div>
                </AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter>
            <Button type="submit" className="bg-[#083CA6] hover:bg-[#062f85]" disabled={loading} data-testid="btn-upd-entrar">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Entrar e atualizar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function ConsultaPage() {
  const [modo, setModo] = useState("protocolo"); // "protocolo" | "cpf"
  const [protocolo, setProtocolo] = useState("");
  const [cpf, setCpf] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const consultar = async (e) => {
    e.preventDefault();
    setLoading(true); setResult(null);
    try {
      // SEC-001: ambos obrigatórios. Backend valida que casam entre si.
      let r;
      if (modo === "protocolo") {
        r = await api.get(`/inscricoes/protocolo/${protocolo.trim()}`,
                           { params: { cpf: onlyDigits(cpf) } });
      } else {
        r = await api.get(`/inscricoes/consulta/cpf`,
                           { params: { cpf: onlyDigits(cpf), protocolo: protocolo.trim() } });
      }
      setResult(r.data);
    } catch (err) {
      logger.error("Falha consulta", err);
      toast.error(err.response?.data?.detail || "Não foi possível localizar a inscrição");
    } finally { setLoading(false); }
  };

  const podeConsultar = protocolo.trim().length > 0 && onlyDigits(cpf).length === 11;

  return (
    <div className="min-h-screen bg-[#F4F6F9]">
      <Header />
      <div className="max-w-3xl mx-auto px-4 py-10">
        <h1 className="text-3xl font-black text-[#083CA6] mb-2">Consultar Inscrição</h1>
        <p className="text-sm text-slate-600 mb-6">
          Consulte pelo número do protocolo ou pelo CPF do responsável.
        </p>
        <Card>
          <CardHeader>
            <CardTitle>Consulta Pública</CardTitle>
            <CardDescription>Escolha como deseja pesquisar sua inscrição.</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={modo} onValueChange={(v) => { setModo(v); setResult(null); }} className="mb-4">
              <TabsList className="grid grid-cols-2 w-full max-w-md" data-testid="consulta-tabs">
                <TabsTrigger value="protocolo" data-testid="tab-protocolo">
                  <Hash className="w-4 h-4 mr-2" />Por Protocolo
                </TabsTrigger>
                <TabsTrigger value="cpf" data-testid="tab-cpf">
                  <IdCard className="w-4 h-4 mr-2" />Por CPF
                </TabsTrigger>
              </TabsList>

              <TabsContent value="protocolo" className="mt-4">
                <form onSubmit={consultar} className="flex gap-3 flex-wrap">
                  <div className="flex-1 min-w-[220px]">
                    <Label className="sr-only">Protocolo</Label>
                    <Input
                      value={protocolo}
                      onChange={(e) => setProtocolo(e.target.value.toUpperCase())}
                      placeholder="GRAJAU-MCMV-2026-000001"
                      data-testid="consulta-input-protocolo"
                    />
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <Label className="sr-only">CPF do titular</Label>
                    <Input
                      value={cpf}
                      onChange={(e) => setCpf(formatCPF(e.target.value))}
                      placeholder="CPF do titular (000.000.000-00)"
                      inputMode="numeric"
                      data-testid="consulta-input-protocolo-cpf"
                    />
                  </div>
                  <Button type="submit" disabled={loading || !podeConsultar}
                          className="bg-[#083CA6] hover:bg-[#062f85]" data-testid="consulta-btn-protocolo">
                    {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                    Consultar
                  </Button>
                </form>
                <p className="text-xs text-slate-500 mt-2">
                  🔒 Por segurança, informe o protocolo <strong>e</strong> o CPF do titular.
                </p>
              </TabsContent>

              <TabsContent value="cpf" className="mt-4">
                <form onSubmit={consultar} className="flex gap-3 flex-wrap">
                  <div className="flex-1 min-w-[200px]">
                    <Label className="sr-only">CPF</Label>
                    <Input
                      value={cpf}
                      onChange={(e) => setCpf(formatCPF(e.target.value))}
                      placeholder="000.000.000-00"
                      inputMode="numeric"
                      data-testid="consulta-input-cpf"
                    />
                  </div>
                  <div className="flex-1 min-w-[220px]">
                    <Label className="sr-only">Protocolo</Label>
                    <Input
                      value={protocolo}
                      onChange={(e) => setProtocolo(e.target.value.toUpperCase())}
                      placeholder="Protocolo (GRAJAU-MCMV-2026-...)"
                      data-testid="consulta-input-cpf-protocolo"
                    />
                  </div>
                  <Button type="submit" disabled={loading || !podeConsultar}
                          className="bg-[#083CA6] hover:bg-[#062f85]" data-testid="consulta-btn-cpf">
                    {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                    Consultar
                  </Button>
                </form>
                <p className="text-xs text-slate-500 mt-2">
                  🔒 Por segurança, informe o CPF <strong>e</strong> o número do protocolo.
                </p>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {result && (
          <Card className="mt-6" data-testid="consulta-resultado">
            <CardHeader>
              <CardTitle>{result.nome_completo}</CardTitle>
              <CardDescription>Protocolo: <span className="font-mono">{result.protocolo}</span></CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex items-center gap-2">
                <span className="font-semibold">Status:</span>
                <Badge className={statusColor(result.status)}>{result.status}</Badge>
              </div>
              <div><span className="font-semibold">Data:</span> {new Date(result.created_at).toLocaleString("pt-BR")}</div>
              <div className="flex flex-wrap gap-2 pt-3 border-t">
                <Button onClick={() => window.open(`${API}/inscricoes/protocolo/${result.protocolo}/comprovante?cpf=${onlyDigits(cpf)}`, "_blank")} className="bg-[#083CA6] hover:bg-[#062f85]" data-testid="btn-baixar-comprovante-consulta">
                  <Download className="w-4 h-4 mr-2" />Baixar Comprovante
                </Button>
                <Button onClick={() => setDialogOpen(true)} className="bg-[#00883A] hover:bg-[#007030]" data-testid="btn-atualizacao-cadastral">
                  <ClipboardEdit className="w-4 h-4 mr-2" />Atualização Cadastral
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {result && <UpdatePasswordDialog open={dialogOpen} onOpenChange={setDialogOpen} protocolo={result.protocolo} />}
      </div>
    </div>
  );
}
